<template>
  <PageLayout>
    <template #header>
      <generic-page-header class="border-0">
        <template #title>{{ $t('Webportal.PerformanceMatrix') }}</template>
        <template #description>{{ $t('Webportal.PerformanceByCountry') }}</template>
        <template #actions-top>
          <SavedViewSelection v-model="selectedView"
                              style="width: 15rem;"
                              :board-setup="boardSetup || {}"
                              :disabled="false"
                              :has-create-button="false"
                              :placeholder="$t('Webportal.ChangeView')"
                               />
<!--
          <FilterItem :filter="fltrs[0]"
                      :rank="0"
                      @click="onShowModalFilter" /> -->

          <LevelSelection v-model="selectedLevel"
                          :options="levelOptions"
                          :placeholder="$t('Webportal.Level')"
                          @update:model-value="onSelectLevel" />

          <FilterItem :filter="fltrs[1]"
                      @click="onShowModalFilter"
                      :rank="1" />

          <BButton  class="options-btn" style="min-width: 7.5rem;"
                    size="sm"
                    variant="outline-primary"
                    @click="onStartClick">
            <BSpinner v-if="isPageLoading && !isMatrixSetupLoading" small />
            <Play  :size="14" v-else />
            {{ isPageLoading ? $t('Webportal.Cancel') : $t('Webportal.Start') }}
          </BButton>

          <BButton  class="options-btn" style="min-width: 7.5rem;"
                    :disabled="true"
                    size="sm"
                    variant="outline-primary">
            <Download :size="16" /> {{ $t('Webportal.Export') }} PPTX
          </BButton>
        </template>
        <!-- <template #actions-bottom>
          <FilterInfo :filter-group="filterGroup" :is-read-only="true" />
        </template> -->
      </generic-page-header>
    </template>

    <template #main>
      <div class="matrix-legend-bar d-flex align-items-center justify-content-between mb-1 ">
        <LegendList />
        <div class="d-flex align-items-center gap-3">
          <RadioButtonGroup v-model="selectedSwitch"
                            :options="switchOptions"
                            :disabled="false" />
        </div>
      </div>

      <Transition name="fadein">
        <MatrixBoard  :board-data="boardData"
                      :board-questions="boardQuestions"
                      :average-data="averageData"
                      :board-setup="boardSetup || {}"
                      :entities="entities"
                      :show-flags="getShowFlags"
                      :selected-mode="selectedSwitch" />
      </Transition>

      <ModalFilter  v-model="currentFilter.showModal"
                    :initial-data="currentFilter.options"
                    :cached-options="currentFilter.cachedOptions"
                    :show-all-options="currentFilter.showAllOptions"
                    :filter-name="currentFilter.key"
                    :placeholder="currentFilter.placeholder"
                    @apply="onChangeModalFilter" />
    </template>
  </PageLayout>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { Download, Funnel, Layers, Package, Globe, Square, Blocks, Play, PackageOpen, SquareDashed, Building2, MapPin } from 'lucide-vue-next';
import PageLayout from '@/componentsv3/PageLayout.vue';
import PageSidebar from '@/componentsv3/PageSidebar.vue';
import RadioButtonGroup from '@/componentsv3/RadioButtonGroup.vue';
import MatrixBoard from '@/componentsv3/Matrix/MatrixBoard.vue';
import ClientSelection from '@/components/ClientSelection.vue';
import CancelToken from '@/mixins/CancelToken';
import LegendList from '@/componentsv3/LegendList.vue';
import UiDataService from '@/services/UiDataService';
import { gatherFilterIds } from '@/utils/treeHelpers';
import { assignKpiPerformanceIndex } from '@/utils/kpiIndexHelpers';
import { getSurveysByType } from '@/utils/surveyHelpers';
import GenericPageHeader from '@/componentsv3/GenericPageHeader.vue';
import SavedViewSelection from '@/componentsv3/SavedViewSelection.vue';
import FilterItem from '@/componentsv3/FilterItem.vue';
import LevelSelection from '@/componentsv3/LevelSelection.vue';
import ModalFilter from '@/componentsv3/ModalFilter.vue';
import FilterInfo from '@/componentsv3/FilterInfo.vue';
import { buildFakeMatrixView, buildFakeMatrixData, buildFakeQuestionList } from '@/assets/matrixFakes';

const GEOGRAPHIC_FILTER_KEYS = ['Regions', 'Subregions', 'NSCs', 'SubNSCs', 'Areas', 'Dealergroups', 'Outlets'];

export default {
  name: 'Matrix',
  mixins: [CancelToken],

  provide() {
    return {
      delegateChangeView: this.inboundChangeView,
    }
  },

  components: {
    ClientSelection,
    MatrixBoard,
    PageLayout,
    PageSidebar,
    RadioButtonGroup,
    LegendList,
    GenericPageHeader,
    SavedViewSelection,
    FilterItem,
    LevelSelection,
    ModalFilter,
    FilterInfo,
    Download, Funnel, Layers, Package, Globe, Square, Blocks, Play, PackageOpen, SquareDashed, Building2, MapPin
  },

  computed: {
    ...mapGetters('auth', [ 'isMSMUser' ]),
    ...mapGetters('clientData', [ 'indices', 'isClientReady', 'surveyIds', 'appLoading', 'structure' ]),
    ...mapGetters('localization', [ 'currentLocale' ]),

    levelOptions() {
      return this.boardSetup?.boards[0]?.Levels || [];
    },

    levelFilter() {
      const filter = this.fltrs.find(f => f.key === this.selectedLevel?.label);
      return filter;
    },

    isPageLoading() {
      return this.pageLoading || this.appLoading;
    },

    brandMap() {
      const map = {};
      const traverse = (items) => {
        for (const item of items) {
          if (item.children?.length) {
            traverse(item.children);
          } else {
            map[item.id] = item;
          }
        }
      };
      if (this.boardSetup?.filters?.Brand) traverse(this.boardSetup.filters.Brand);
      return map;
    },

    filterGroup() {
      const ids = this.selection.Brand || [];
      let tags = ids.map(id => this.brandMap[id]).filter(Boolean).map(b => ({ id: b.id, label: b.label }));
      return { label: 'Brand', tags: tags };
    },

    getShowFlags() {
      if (!this.selectedLevel) return false;
      return this.selectedLevel.group == 0 || (this.selectedLevel.id > 1 && this.selectedLevel.id < 4);
    },

    fltrs() {
      return [
        { key: 'Brand',           icon: Funnel,        label: this.$t('Webportal.BrandFilter.Placeholder') },
        { key: 'Waves',           icon: Globe,        label: this.$t('Webportal.WaveFilter.Placeholder') },
        { key: 'Regions',         icon: Square,       label: this.$t('Webportal.RegionFilter.Placeholder') },
        { key: 'Subregions',      icon: Blocks,       label: this.$t('Webportal.SubregionFilter.Placeholder') },
        { key: 'NSCs',            icon: Package,      label: this.$t('Webportal.DashboardBMW.NSCFilter.Placeholder') },
        { key: 'SubNSCs',         icon: PackageOpen,  label: this.$t('Webportal.DashboardBMW.NSCSubregionFilter.Placeholder') },
        { key: 'Areas',           icon: SquareDashed, label: this.$t('Webportal.AreaFilter.Placeholder') },
        { key: 'Dealergroups',    icon: Building2,    label: this.$t('Webportal.DashboardBMW.DealergroupFilter.Placeholder') },
        { key: 'ClientLocations', icon: MapPin,       label: this.$t('Webportal.DashboardBMW.OutletFilter.Placeholder') },
      ];
    },

    switchOptions() {
      return [
        { value: 'all', label: this.$t('Webportal.Matrix.AllDetails') },
        { value: 'medium', label: this.$t('Webportal.Matrix.Medium') },
        { value: 'minimal', label: this.$t('Webportal.Matrix.Minimal') }
      ];
    },

  },

  data() {
    return {
      pageLoading: false,
      isMatrixSetupLoading: false,

      averageData: [],

      boardCancelToken: null,
      boardSetup: undefined,
      entities: [],
      boardData: { real: null, avg: null },
      boardQuestions: { real: null, avg: null },

      activeViewId: null,
      selection: {},
      selectedLevel: null,
      selectedView: null,
      selectedSwitch: 'medium',

      // der Filter der aktuell im Modal angezeigt und angepasst werden kann vom User
      currentFilter: {
        showModal: false,
        options: [],
        cachedOptions: null,
        showAllOptions: false,
        key: null,
        label: this.$t('Webportal.NotAvailable'),
      },

    }
  },

  async mounted() {
    if (!this.isMatrixSetupLoading) {
      this.loadMatrixSetup({ load: true });
    }
  },


  beforeUnmount() {
    this.cancelCancelToken(this.boardCancelToken);
    this.setAppLoading(false);
  },

  watch: {
    currentLocale(newVal, oldVal) {
      if (oldVal && newVal !== oldVal && this.boardSetup) {
        this.loadMatrixSetup({ load: true });
      }
    }
  },

  methods: {
    ...mapActions('clientData', [ 'setAppLoading', 'setHasData' ]),
    getSurveysByType,

    onStartClick() {
      if (this.pageLoading) {
        this.cancelCancelToken(this.boardCancelToken);
        this.markAllControlsLoaded();
        this.setAppLoading(false);
        this.pageLoading = false;
      } else {
        this.loadMatrix();
      }
    },

    markAllControlsLoaded() {
      const mark = (items) => {
        for (const item of items) {
          item.isLoaded = true;
          if (item.children?.length) mark(item.children);
        }
      };
      if (this.boardSetup?.boards?.[0]?.controls) {
        mark(this.boardSetup.boards[0].controls);
      }
    },

    getSurveys(surveys, brandsRaw) {
      const brands = brandsRaw.map(b => /^[nu]/i.test(b) ? b.slice(1) : b);
      const type = /^n/i.test(brandsRaw[0]) ? 'n' : /^u/i.test(brandsRaw[0]) ? 'u' : null;
      const sur = surveys[0].children || [];

      let result = [];
      for (const brand of brands) {
        for (const survey of sur) {
          if (survey.BrandId.includes(brand)) {
            for (const id of [].concat(survey.id).flat()) {
              if (!result.includes(id)) result.push(id);
            }
          }
        }
      }

      return this.getSurveysByType(type, result);
    },

    doSelection() {
      if (this.selectedLevel.id === 'G6/B6') {
        this.selection.Hint = this.boardSetup.boards[0]?.G6B6FilterData || [];
      } else if (this.selectedLevel.id === 'Default') {
        this.selection.Hint = this.boardSetup.boards[0]?.DefaultFilterData || [];
      } else if (this.selectedLevel.id === 'Global') {
        this.selection.Hint = [];
      } else {
        let numeric = parseInt(this.selectedLevel.id);
        if (isNaN(numeric) || numeric < 1 || numeric > 6 ) {
          log.error("Matrix: Unknown level", this.selectedLevel.id);
        }
      }
    },

    getBaseCalls() {
      const board = this.boardSetup?.boards[0];
      if (!board) return [];

      const flattenControls = (items) => items
        .filter(x => x.mode !== 'question' && x.mode !== 'answer')
        .flatMap(x => [
          {
            kpiId: x.kpiId,
            kpiCode: x.kpiCode,
            kpiName: x.kpiName,
            controlId: x.id,
            isKpiGroup: x.isKpiGroup,
            showQuestions: x.showQuestions
          },
          ...flattenControls(x.children ?? [])
        ]);
      return flattenControls(board.controls);
    },

    findControl(nodes, kpiId, controlId) {
      for (const node of nodes) {
        if (node.kpiId === kpiId && node.id === controlId) return node;
        if (node.children?.length) {
          const found = this.findControl(node.children, kpiId, controlId);
          if (found) return found;
        }
      }
      return null;
    },

    applyResultToControl(x) {
      if (!x) return;

      const control = this.findControl(this.boardSetup.boards[0].controls, x.kpiId, x.controlId);
      if (!control) {
        // log.error(`Control not found for kpiId ${x.kpiId}, controlId ${x.controlId}`);
        return;
      }

      if (!x.KPIGroup && !x.KPI) {
        control.label = !control.label ? this.$t('Webportal.NotAvailable') : control.label;
        control.isLoaded = true;
        // Wenn true:  Dann zeige nur das Label an. KEINE KPI Werte, auch wenn diese vorhanden sind!!!!!!!!!!!!
        // Wenn es keine Daten gibt und nicht per "ShowKpiLabelMode" die Anzeige forciert werden soll,
        // dann Control verstecken
        control.hidden = control.ShowKpiLabelMode ? false : true;
        return;
      }
      control.isLoaded = true;
      control.hidden = false;

      // set tooltips
      if (x.KPIGroup && x.KPIGroup.length > 0) {
        control.Tooltip = x.KPIGroup[0].KpiGroupDescription;
      } else if (x.KPI && x.KPI.length > 0) {
        control.Tooltip = x.KPI[0].KpiDescription;
      }

      if (x.NumberOfShopsPerWave?.length) {
        for (const shop of x.NumberOfShopsPerWave) {
          const entity = this.entities.find(e => !e.isAverage && e.label === shop.AggregationLevel);
          if (!entity) continue;
          const wave = entity.waves.find(w => w.label === shop.SurveyWaveName);
          if (!wave) continue;
          wave.SurveyInstanceCount = shop.SurveyInstanceCount;
        }
      }
    },

    applyAverageWaveCounts(result) {
      if (!result?.NumberOfShopsPerWave?.length) return;
      const avgEntity = this.entities.find(e => e.isAverage);
      if (!avgEntity) return;
      for (const shop of result.NumberOfShopsPerWave) {
        const wave = avgEntity.waves.find(w => w.label === shop.SurveyWaveName);
        if (wave) wave.SurveyInstanceCount = shop.SurveyInstanceCount;
      }
    },

    getCustomProps(level) {
      if (level.group === '0') {
        return this.selection.Hint?.map(h => h.id).join('|') || null;
      }
      return this.selectedLevel?.label !== 'Outlets'
        ? this.selection[this.selectedLevel?.label]?.join('|') || this.selection.Hint?.map(x => x.id).join('|')
        : null;
    },

    getBaseParams() {
      return {
        brandIds:               this.selection.Brand?.join('|'),
        matrixId:               this.boardSetup?.id,
        surveyIds:              this.selection.Surveys?.join(','),
        customPropertyValueIds: this.getCustomProps(this.selectedLevel),
        clientLocationIds:      this.selectedLevel?.label === 'Outlets' ? this.selection.Outlets?.join(',') : null,
        waveIds:                this.selection.Waves?.join(','),
        localizationId:         this.currentLocale
      };
    },

    async loadMatrixSetup({ viewChanged = false, load = false } = {}) {
      this.pageLoading = this.isMatrixSetupLoading = true;
      if (this.appLoading) {
        this.pageLoading = this.isMatrixSetupLoading = false;
        return;
      }
      this.setAppLoading(true);
      this.setHasData(false);
      this.boardData = { real: null, avg: null };
      this.boardQuestions = { real: null, avg: null };
      this.averageData = [];
      this.boardCancelToken = this.initCancelToken(this.boardCancelToken);

      // fetch board setup
      if (viewChanged) {
        this.boardSetup.boards[0] = JSON.parse(JSON.stringify(this.boardSetup.views.find(v => v.id === this.activeViewId)));
        this.boardSetup.id = this.activeViewId;
      } else {
        this.boardSetup = buildFakeMatrixView();
        if (!this.boardSetup || !this.boardSetup.views || !this.boardSetup.views.length) {
          this.setAppLoading(false);
          this.pageLoading = this.isMatrixSetupLoading = false;
          return;
        }
      }


      if(!this.selection.Brand || this.selection.Brand.length === 0) {
        this.selection.Brand = gatherFilterIds(this.boardSetup.filters.Brand);
      }
      this.selection.Surveys = this.getSurveys(this.boardSetup.filters.Surveys, this.selection.Brand);
      if (!this.selection.Surveys || this.selection.Surveys.length === 0)
        this.selection.Surveys = gatherFilterIds(this.boardSetup.filters.Surveys);
      if (!this.selection.Waves || this.selection.Waves.length === 0)
        this.selection.Waves = [...this.boardSetup.filters.Waves]
          .sort((a, b) => b.WaveDate > a.WaveDate ? 1 : -1)
          .slice(0, 2)
          .map(w => w.id);



      assignKpiPerformanceIndex(this.boardSetup.boards[0].controls);

      this.activeViewId = this.boardSetup?.id || null;

      this.selectedView = !this.selectedView
        ? this.boardSetup.views.find(v => v.id === this.boardSetup.id) || this.boardSetup.views[0]
        : this.boardSetup.views.find(v => v.id === this.selectedView.id);

      // View changed => reset level + filter selection
      if (viewChanged) {
        this.selectedLevel = null;
        this.selection.Hint = null;
      }
      // Predefined level — re-sync with new Levels array after boardSetup reload
      if (this.selectedLevel) {
        this.selectedLevel = this.boardSetup.boards[0].Levels.find(x => x.id === this.selectedLevel.id) || null;
      }
      if (!this.selectedLevel) {
        this.selectedLevel = this.boardSetup.boards[0].Levels.find(x => x.id === 'Default' && x.isEnabled)
          || this.boardSetup.boards[0].Levels.find(x => x.group === '0' && x.isEnabled)
          || this.boardSetup.boards[0].Levels.find(x => x.isEnabled)
          || null;
        if (!this.selectedLevel) {
          this.setAppLoading(false);
          this.pageLoading = this.isMatrixSetupLoading = false;
          return;
        }
      }

      if (!this.selection.LevelId) {
        var list = this.selectedLevel.id === 'G6/B6' ? this.boardSetup.boards[0].G6B6FilterData : this.boardSetup.boards[0].DefaultFilterData;
        this.selection.LevelId = list?.map(x => x.id);
        this.selection.Hint = list;
      }

      this.setAppLoading(false);
      this.pageLoading = this.isMatrixSetupLoading = false;

      if (load) this.loadMatrix();
    },

    async loadMatrix() {
      if (!this.boardSetup) return;

      this.pageLoading = true;
      this.setHasData(false);
      this.boardData = { real: null, avg: null };
      this.boardQuestions = { real: null, avg: null };
      this.averageData = [];
      this.boardCancelToken = this.initCancelToken(this.boardCancelToken);

      this.doSelection();
      this.entities = this.getEntities(this.selection.Hint || [], this.selection.Waves || [], this.boardSetup.filters.Waves || []);
      if (this.entities.length === 0) {
        this.setHasData(true);
        this.pageLoading = false;
        return;
      }

      await this.fetchMatrixData();

      this.pageLoading = false;
    },

    async fetchMatrixData() {
      const params = this.getBaseParams();
      const hasRealEntities = this.entities.some(e => !e.isAverage);
      const hasAverage = this.entities.some(e => e.isAverage);
      const calls = this.getBaseCalls();

      this.boardData = [];
      this.averageData = [];

      const buildEnriched = (call, superData) => {
        const prop = call.isKpiGroup ? 'KPIGroup' : 'KPI';
        const item = call.kpiCode ? (superData?.[prop] || []).find(x => x.KpiCode === call.kpiCode) : null;
        return {
          KPI:                  call.isKpiGroup ? null : (item ? [item] : null),
          KPIGroup:             call.isKpiGroup ? (item ? [item] : null) : null,
          NumberOfShopsPerWave: superData?.NumberOfShopsPerWave,
          kpiId:                call.kpiId,
          isKpiGroup:           call.isKpiGroup,
          controlId:            call.controlId
        };
      };

      const [realData, avgData, realQuestions, avgQuestions] = await Promise.all([
        hasRealEntities ? Promise.resolve(buildFakeMatrixData({ hasAverage: false })) : Promise.resolve(null),
        hasAverage      ? Promise.resolve(buildFakeMatrixData({ hasAverage: true }))  : Promise.resolve(null),
        hasRealEntities ? Promise.resolve(buildFakeQuestionList({ hasAverage: false })) : Promise.resolve(null),
        hasAverage      ? Promise.resolve(buildFakeQuestionList({ hasAverage: true }))  : Promise.resolve(null)
      ]);

      this.boardData = { real: realData, avg: avgData };
      this.boardQuestions = { real: realQuestions, avg: avgQuestions };

      if (avgData) {
        this.applyAverageWaveCounts(avgData);
        for (const call of calls) {
          const enriched = buildEnriched(call, avgData);
          if (!hasRealEntities) this.applyResultToControl(enriched);
        }
        if (!hasRealEntities) this.setHasData(true);
      }

      if (realData) {
        for (const call of calls) {
          const enriched = buildEnriched(call, realData);
          this.applyResultToControl(enriched);
        }
        this.setHasData(true);
      }

      this.integrateQuestions();

      return this.boardData;
    },

    onSelectLevel(opt) {
      this.selection.LevelId = undefined;
      this.selection.Hint = undefined;
      this.selectedLevel = opt;
      if (opt.group !== '0') {
        this.onShowModalFilter({ key: opt.label, label: opt.label, icon: Layers });
      }
    },

    inboundChangeView(newView) {
      this.selection.Hint = undefined;
      this.selection.LevelId = undefined;
      this.boardSetup.boards[0] = JSON.parse(JSON.stringify(this.boardSetup.views.find(v => v.id === newView.id)));
      this.activeViewId = newView.id;
      this.loadMatrixSetup({ viewChanged: true, load: false });
    },

    // Filters
    onChangeModalFilter(name, items, selected) {
      this.selection = {
        Brand: this.selection.Brand,
        Surveys: this.selection.Surveys,
        Waves: this.selection.Waves,
        Hint: this.selection.Hint,
        [name]: selected
      };
      if (GEOGRAPHIC_FILTER_KEYS.includes(name)) {
        this.selection.Hint = items.filter(i => selected.includes(i.id));
      }

      if (name === 'Brand') {
        this.selectedLevel = null;
        this.selection.LevelId = undefined;
        this.selection.Hint = undefined;
        this.loadMatrixSetup({ viewChanged: false, load: false });
      }
    },

    onShowModalFilter(filter) {
      if (!this.boardSetup?.filters) return;

      let options = filter.customIndex && filter.customIndex >= 0 ? this.boardSetup.filters.customProperties[filter.customIndex] : this.boardSetup.filters[filter.key];
      let cachedOptions = null;
      if (GEOGRAPHIC_FILTER_KEYS.includes(filter.key) && this.boardSetup?.filters?.[filter.key]) {
        const raw = this.boardSetup.filters[filter.key];
        cachedOptions = Array.isArray(raw) ? raw : [raw];
      }

      const levelSelectionIds = this.selection[this.selectedLevel?.label];
      const isLevelFilter = filter.key === this.selectedLevel?.label;
      if (options && isLevelFilter && levelSelectionIds?.length) {
        options = options.filter(x => levelSelectionIds.includes(x.id)); // Filtert die Optionen basierend auf der aktuellen Level-Auswahl, damit nur relevante Optionen im Modal angezeigt werden
      }
      if (filter.key === 'Waves') {
        options.forEach(opt => {
          opt.checked = this.selection.Waves?.includes(opt.id) ?? false;
        });
      } else if (filter.key === 'Brand') {
        const markBrandChecked = (nodes) => {
          for (const node of nodes) {
            if (node.children?.length) {
              markBrandChecked(node.children);
              node.checked = node.children.some(c => c.checked);
            } else {
              const ids = [].concat(node.id).flat();
              node.checked = ids.some(id => this.selection.Brand?.includes(id));
            }
          }
        };
        markBrandChecked(Array.isArray(options) ? options : [options]);
      } else if (filter.key === 'Outlets') {
        options = this.boardSetup.filters['ClientLocations'] || [];
        options.forEach(opt => {
          opt.checked = this.selection.Hint?.some(h => h.id === opt.id)
                     ?? this.selection[filter.key]?.includes(opt.id)
                     ?? false;
        });
      } else {
        options.forEach(opt => {
          opt.checked = this.selection.Hint?.some(h => h.id === opt.id)
                     ?? this.selection[filter.key]?.includes(opt.id)
                     ?? false;
        });
      }

      // Waves: Einträge in umgekehrter Reihenfolge anzeigen
      const orderedOptions = filter.key === 'Waves'
        ? [...(Array.isArray(options) ? options : [options])].reverse()
        : Array.isArray(options) ? options : [options];

      this.currentFilter = {
        options: orderedOptions,
        cachedOptions,
        showAllOptions: cachedOptions !== null,
        key: filter.key,
        label: filter.label,
        showModal: true
      };
    },

    integrateQuestions() {
      const realEntityIds = this.entities.filter(e => !e.isAverage).map(e => e.id);
      const hasAvg = this.entities.some(e => e.isAverage);
      // Verfügbare Datenquelle: real bevorzugt, Fallback auf avg (Question-Daten sind nicht entity-spezifisch)
      const realSource = this.boardQuestions.real || this.boardQuestions.avg;
      const avgSource = this.boardQuestions.avg || this.boardQuestions.real;

      const getKpiQuestions = (data, kpiCode, isKpiGroup) => {
        const prop = isKpiGroup ? 'KPIGroup' : 'KPI';
        return data?.[prop]?.find(x => x.KpiCode === kpiCode)?.Questions || [];
      };

      const collectWaves = (waveEntries, entityIds, valueFn) => {
        const result = {};
        for (const w of waveEntries)
          for (const id of entityIds)
            result[id + '§' + w.SurveyWaveName] = valueFn(w);
        return result;
      };

      const buildQWaves = (kpiCode, isKpiGroup, questionIndex) => {
        const getWaves = (data) => getKpiQuestions(data, kpiCode, isKpiGroup)[questionIndex]?.Waves || [];
        const valueFn = w => ({ n: w.QuestionSurveyInstanceCount });
        return {
          ...(hasAvg ? collectWaves(getWaves(avgSource), ['__average__'], valueFn) : {}),
          ...(realEntityIds.length ? collectWaves(getWaves(realSource), realEntityIds, valueFn) : {})
        };
      };

      const buildAWaves = (kpiCode, isKpiGroup, questionIndex, answerIndex) => {
        const getWaves = (data) => getKpiQuestions(data, kpiCode, isKpiGroup)[questionIndex]?.Answers?.[answerIndex]?.Waves || [];
        const valueFn = w => ({ value: w.CountPercent, n: w.SurveyInstanceCount });
        return {
          ...(hasAvg ? collectWaves(getWaves(avgSource), ['__average__'], valueFn) : {}),
          ...(realEntityIds.length ? collectWaves(getWaves(realSource), realEntityIds, valueFn) : {})
        };
      };

      const addToLeaves = (controls) => {
        for (const control of controls) {
          // Bereits vorhandene Question-Children überschreiben (bei erneutem Aufruf)
          if (control.children?.length > 0 && control.children[0]?.mode === 'question') {
            control.children = null;
          }
          if (control.children?.length > 0) { addToLeaves(control.children); continue; }
          if (control.mode !== 'kpi' || !control.showQuestions) continue;

          const rawQ = getKpiQuestions(avgSource, control.kpiCode, control.isKpiGroup).length
            ? getKpiQuestions(avgSource, control.kpiCode, control.isKpiGroup)
            : getKpiQuestions(realSource, control.kpiCode, control.isKpiGroup);

          if (!rawQ.length) continue;

          control.children = rawQ.map((q, qi) => ({
            id:       `q_${control.id}_${qi}`,
            label:    [q.QuestionNumber, q.QuestionText].join(' '),
            kpiCode:  control.kpiCode,
            isKpiGroup: control.isKpiGroup,
            number:   q.QuestionNumber,
            mode:     'question',
            isOpen:   false,
            isLoaded: true,
            _waves:   buildQWaves(control.kpiCode, control.isKpiGroup, qi),
            children: (q.Answers || []).map((a, ai) => ({
              id:       `a_${control.id}_${qi}_${ai}`,
              label:    a.AnswerText,
              kpiCode:  control.kpiCode,
              isKpiGroup: control.isKpiGroup,
              number:   q.QuestionNumber,
              mode:     'answer',
              isOpen:   false,
              isLoaded: true,
              _waves:   buildAWaves(control.kpiCode, control.isKpiGroup, qi, ai),
              children: null
            }))
          }));
        }
      };
      addToLeaves(this.boardSetup.boards[0].controls);
    },

    getEntities(columns, waveIds, allWaves) {
      const waves = allWaves.filter(w => waveIds.includes(w.id))
        .sort((a, b) => b.SortOrder < a.SortOrder ? 1 : -1)
        .slice(-4);
      let result = columns.map(col => ({ ...col, waves: waves.map(w => ({ ...w })) }));

      // Durchschnitt-Entity nur bei "All accessible Data" (key=null)
      const showAverage = this.selectedLevel?.key === null || this.selectedLevel?.group !== '0';
      if (showAverage) {
        let avgLabel = columns.length > 0
          ? this.$t('Webportal.Grouped') + ' ' + this.selectedLevel?.key
          : this.$t('Webportal.Matrix.AllData');

        // Sonderlocke NUR für avg von Outlets ... hier BuNo's concat
        // if (this.selectedLevel.group === '1' && this.selectedLevel.id === '6') {
        //   const buNos = this.selection.Outlets
        //     ?.map(id => this.boardSetup.filters.ClientLocations.find(cl => cl.id === id)?.buNo)
        //     .filter(Boolean);
        //   avgLabel = buNos.join(', ');
        // }

        result.unshift({
          id: '__average__',
          label: avgLabel,
          isAverage: true,
          waves: waves.map(w => ({ ...w }))
        });
      }

      return result;
    }
  }
}
</script>

<style lang="scss" scoped>
.matrix-legend-bar {
  flex-shrink: 0;
}
</style>
